import { PDFDocument, rgb, StandardFonts } from 'pdf-lib'
import { aiExtractLatestText, aiSendQualificacaoMessage, aiWaitForQualificacaoResult } from './ai_helper'
import { renderIconToPng, FaBalanceScale, FaFileAlt, FaFileContract } from './pdf_icon_utils'

const AI_GUEST_ID_KEY = 'AI_GUEST_USER_ID'

// ─── Utilities ────────────────────────────────────────────────────────────────

function hexToRgb(hex) {
  if (!hex) return rgb(0, 0, 0)
  const clean = hex.replace('#', '')
  return rgb(
    parseInt(clean.substring(0, 2), 16) / 255,
    parseInt(clean.substring(2, 4), 16) / 255,
    parseInt(clean.substring(4, 6), 16) / 255
  )
}

function sanitizeTextForPDF(text) {
  if (!text) return ''
  return String(text)
    .replace(/\r\n/g, ' ')
    .replace(/\n/g, ' ')
    .replace(/\r/g, ' ')
    .replace(/\t/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

function wrapText(text, maxWidth, font, fontSize) {
  const sanitized = sanitizeTextForPDF(text)
  const words = sanitized.split(' ')
  const lines = []
  let current = ''
  for (const word of words) {
    const test = current ? `${current} ${word}` : word
    if (font.widthOfTextAtSize(test, fontSize) > maxWidth) {
      if (current) { lines.push(current); current = word }
      else { lines.push(word); current = '' }
    } else {
      current = test
    }
  }
  if (current) lines.push(current)
  return lines
}

function wrapTextInCell(text, maxWidth, font, fontSize) {
  const sanitized = sanitizeTextForPDF(text)
  const words = sanitized.split(' ')
  const lines = []
  let current = ''
  for (const word of words) {
    if (font.widthOfTextAtSize(word, fontSize) > maxWidth) {
      if (current) { lines.push(current); current = '' }
      let charLine = ''
      for (const char of word) {
        const test = charLine + char
        if (font.widthOfTextAtSize(test, fontSize) > maxWidth && charLine) {
          lines.push(charLine); charLine = char
        } else {
          charLine = test
        }
      }
      if (charLine) current = charLine
    } else {
      const test = current ? `${current} ${word}` : word
      if (font.widthOfTextAtSize(test, fontSize) > maxWidth) {
        if (current) lines.push(current)
        current = word
      } else {
        current = test
      }
    }
  }
  if (current) lines.push(current)
  return lines.length > 0 ? lines : ['']
}

function drawRoundedRect(page, x, y, width, height, radius, borderColor, borderWidth = 2) {
  page.drawLine({ start: { x: x + radius, y: y + height }, end: { x: x + width - radius, y: y + height }, thickness: borderWidth, color: borderColor })
  page.drawLine({ start: { x: x + width, y: y + height - radius }, end: { x: x + width, y: y + radius }, thickness: borderWidth, color: borderColor })
  page.drawLine({ start: { x: x + width - radius, y: y }, end: { x: x + radius, y: y }, thickness: borderWidth, color: borderColor })
  page.drawLine({ start: { x: x, y: y + radius }, end: { x: x, y: y + height - radius }, thickness: borderWidth, color: borderColor })
  const steps = 60
  // pdf-lib uses Y-up: all corners go clockwise → sweep = -π/2
  const corners = [
    { cx: x + radius,         cy: y + height - radius, startAngle: Math.PI,       sweep: -Math.PI / 2 }, // top-left
    { cx: x + width - radius, cy: y + height - radius, startAngle: Math.PI / 2,   sweep: -Math.PI / 2 }, // top-right
    { cx: x + width - radius, cy: y + radius,          startAngle: 0,             sweep: -Math.PI / 2 }, // bottom-right
    { cx: x + radius,         cy: y + radius,          startAngle: -Math.PI / 2,  sweep: -Math.PI / 2 }, // bottom-left
  ]
  for (const { cx, cy, startAngle, sweep } of corners) {
    for (let i = 0; i < steps; i++) {
      const a1 = startAngle + sweep * (i / steps)
      const a2 = startAngle + sweep * ((i + 1) / steps)
      page.drawLine({
        start: { x: cx + radius * Math.cos(a1), y: cy + radius * Math.sin(a1) },
        end: { x: cx + radius * Math.cos(a2), y: cy + radius * Math.sin(a2) },
        thickness: borderWidth, color: borderColor,
      })
    }
  }
}

function drawFilledRoundedRect(page, x, y, width, height, radius, fillColor) {
  // Two overlapping rects form a cross, circles fill the rounded corners
  page.drawRectangle({ x: x + radius, y, width: width - radius * 2, height, color: fillColor })
  page.drawRectangle({ x, y: y + radius, width, height: height - radius * 2, color: fillColor })
  for (const [cx, cy] of [
    [x + radius,         y + radius],
    [x + width - radius, y + radius],
    [x + radius,         y + height - radius],
    [x + width - radius, y + height - radius],
  ]) {
    page.drawCircle({ x: cx, y: cy, size: radius, color: fillColor, borderWidth: 0 })
  }
}

async function loadImageAsArrayBuffer(url) {
  try {
    const response = await fetch(url)
    if (!response.ok) throw new Error(`Failed: ${url}`)
    return await response.arrayBuffer()
  } catch {
    return null
  }
}

// ─── AI helpers ───────────────────────────────────────────────────────────────

function resolveAiUserId() {
  try {
    const raw = localStorage.getItem('authUser') || window.sessionStorage.getItem('authUser')
    if (raw) {
      const obj = JSON.parse(raw)
      const id = obj?.id ?? obj?.uid ?? obj?.userId ?? obj?.user_id ?? ''
      if (id) return String(id)
    }
  } catch (_) {}
  try {
    let guestId = window.sessionStorage.getItem(AI_GUEST_ID_KEY)
    if (!guestId) {
      const uuid = window.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(16).slice(2)}`
      guestId = `guest-${uuid}`
      try { window.sessionStorage.setItem(AI_GUEST_ID_KEY, guestId) } catch (_) {}
    }
    return guestId
  } catch (_) { return '' }
}

function truncateForPrompt(text, maxLength = 400) {
  if (!text) return ''
  const normalized = String(text).replace(/\s+/g, ' ').trim()
  return normalized.length <= maxLength ? normalized : `${normalized.slice(0, maxLength - 3)}...`
}

function buildAiPrompt(processo, publicacao) {
  if (!processo) return ''
  const parts = []
  if (processo.NumeroCNJ) parts.push(`Número CNJ: ${processo.NumeroCNJ}.`)
  if (processo.Situacao) parts.push(`Situação atual: ${processo.Situacao}.`)
  if (processo.ClassificacaoProcesso) parts.push(`Classificação: ${processo.ClassificacaoProcesso}.`)
  if (processo.Observacao) parts.push(`Observações: ${truncateForPrompt(processo.Observacao, 200)}.`)
  const lead = processo.Lead || {}
  if (lead.Reclamante) parts.push(`Reclamante: ${lead.Reclamante}.`)
  if (lead.ReuRazaoSocial) parts.push(`Réu: ${lead.ReuRazaoSocial}.`)
  if (lead.ValorCausa) parts.push(`Valor da causa: ${lead.ValorCausa}.`)
  if (lead.Tribunal) parts.push(`Tribunal: ${lead.Tribunal}.`)
  if (lead.Vara) parts.push(`Vara: ${lead.Vara}.`)
  if (lead.Esfera) parts.push(`Esfera: ${lead.Esfera}.`)
  if (lead.ObjetoSegurado) parts.push(`Objeto segurado: ${lead.ObjetoSegurado}.`)
  if (publicacao) {
    if (publicacao.Caderno) parts.push(`Caderno: ${publicacao.Caderno}.`)
    if (publicacao.Resumo) parts.push(`Resumo existente: ${truncateForPrompt(publicacao.Resumo, 300)}.`)
    if (publicacao.Descricao) parts.push(`Descrição: ${truncateForPrompt(publicacao.Descricao, 300)}.`)
  }
  return `Com base nos dados do processo id ${processo.Id || ''}, gere uma resposta em JSON com as chaves "nova_emissao" e "objeto_acao". ` +
    'Use linguagem jurídica objetiva em português. ' +
    '"nova_emissao" deve explicar, em até 2 parágrafos curtos, a oportunidade de nova emissão de garantia ou próximos passos financeiros relevantes. ' +
    '"objeto_acao" deve resumir, em até 3 parágrafos curtos, o objeto principal da ação judicial, fundamento legal e status atual. ' +
    'Não inclua texto fora do JSON. ' +
    (parts.length ? `Dados úteis: ${parts.join(' ')}` : '')
}

function safeParseAiJson(text) {
  if (!text) return null
  let payload = String(text).trim()
  if (payload.startsWith('```')) {
    const parts = payload.split('```')
    payload = parts.length >= 3 ? parts[1] : payload.replace(/^```(?:json)?/i, '').replace(/```$/i, '').trim()
  }
  const match = payload.match(/\{[\s\S]*\}/)
  try { return JSON.parse(match ? match[0] : payload) } catch { return null }
}

async function fetchAiResumo(processo, publicacao) {
  if (!processo?.Id) return null
  const userId = resolveAiUserId()
  if (!userId) return null
  const prompt = buildAiPrompt(processo, publicacao)
  if (!prompt) return null
  try {
    const postResp = await aiSendQualificacaoMessage({ message: prompt, userId, idProcesso: processo.Id, conversationId: '' })
    const jobId = postResp?.id || ''
    if (!jobId) return null
    const final = await aiWaitForQualificacaoResult(jobId, { userId, pollIntervalMs: 800, maxWaitMs: 120000 })
    const answer = aiExtractLatestText(final)
    const parsed = safeParseAiJson(answer)
    if (!parsed) return null
    return { novaEmissao: parsed.nova_emissao || '', objetoAcao: parsed.objeto_acao || '' }
  } catch { return null }
}

// ─── Main generator ───────────────────────────────────────────────────────────

/**
 * Gera o Insight Report em PDF usando o template ativo da seguradora.
 * @param {Object} processo - Dados completos do processo
 * @param {Object} template - { CorPrimaria, CorSecundaria, LogoPath } do template ativo
 * @param {Object|null} publicacao - Publicação selecionada (opcional)
 * @returns {Promise<Blob>}
 */
export async function generateInsightReport(processo, template = {}, publicacao = null) {
  const primaryColor = hexToRgb(template.CorPrimaria || '#B68749')
  const secondaryColor = hexToRgb(template.CorSecundaria || '#f5e6d0')

  const pdfDoc = await PDFDocument.create()
  const page = pdfDoc.addPage([595.28, 841.89])
  const { width, height } = page.getSize()

  const fontRegular = await pdfDoc.embedFont(StandardFonts.Helvetica)
  const fontBold = await pdfDoc.embedFont(StandardFonts.HelveticaBold)

  // ── Logo ──────────────────────────────────────────────────────────────────
  let logoImage = null
  let logoWidth = 0
  let logoHeight = 0

  if (template.LogoPath) {
    const logoUrl = template.LogoPath
    const logoBytes = await loadImageAsArrayBuffer(logoUrl)
    if (logoBytes) {
      try {
        const ext = template.LogoPath.split('.').pop().toLowerCase()
        logoImage = ext === 'jpg' || ext === 'jpeg'
          ? await pdfDoc.embedJpg(logoBytes)
          : await pdfDoc.embedPng(logoBytes)
        const dims = logoImage.scale(1)
        let scale = 130 / dims.width
        logoWidth = dims.width * scale
        logoHeight = dims.height * scale
        if (logoHeight > 52) {
          const hScale = 52 / logoHeight
          logoWidth *= hScale
          logoHeight = 52
        }
      } catch { logoImage = null }
    }
  }

  // ── Icons ─────────────────────────────────────────────────────────────────
  const iconColor = template.CorPrimaria || '#B68749'
  const iconRenderSize = 64  // render high-res, display smaller
  const iconSize = 22

  let lawIcon = null
  let paperIcon = null
  let paperAltIcon = null

  try {
    const [lawBytes, paperBytes, paperAltBytes] = await Promise.all([
      renderIconToPng(FaBalanceScale, iconColor, iconRenderSize),
      renderIconToPng(FaFileAlt, iconColor, iconRenderSize),
      renderIconToPng(FaFileContract, iconColor, iconRenderSize),
    ])
    if (lawBytes) lawIcon = await pdfDoc.embedPng(lawBytes)
    if (paperBytes) paperIcon = await pdfDoc.embedPng(paperBytes)
    if (paperAltBytes) paperAltIcon = await pdfDoc.embedPng(paperAltBytes)
  } catch { /* icons optional */ }

  // ── Layout constants ──────────────────────────────────────────────────────
  const margin = 20
  const containerPadX = 15
  const containerPadY = 20
  const borderRadius = 10
  const headerHeight = 100
  const footerHeight = 50
  const textColor = rgb(0.2, 0.2, 0.2)

  // ── Header ────────────────────────────────────────────────────────────────
  page.drawRectangle({ x: 0, y: height - headerHeight, width, height: headerHeight, color: rgb(1, 1, 1) })

  page.drawText(sanitizeTextForPDF(`Processo:   ${processo.NumeroCNJ || ''}`), {
    x: 30, y: height - 25, size: 11, font: fontRegular, color: rgb(0, 0, 0),
  })

  if (logoImage) {
    page.drawImage(logoImage, {
      x: width - logoWidth - 30,
      y: height - headerHeight + (headerHeight - logoHeight) / 2,
      width: logoWidth, height: logoHeight,
    })
  }

  page.drawText('Insight Report', { x: 30, y: height - 55, size: 28, font: fontBold, color: rgb(0, 0, 0) })
  page.drawText('Oportunidade Garantia Judicial', { x: 30, y: height - 80, size: 14, font: fontRegular, color: primaryColor })

  // Header bottom line
  page.drawLine({ start: { x: 0, y: height - headerHeight }, end: { x: width, y: height - headerHeight }, thickness: 2, color: primaryColor })

  // ── Footer ────────────────────────────────────────────────────────────────
  page.drawRectangle({ x: 0, y: 0, width, height: footerHeight, color: primaryColor })

  // ── Info grid ─────────────────────────────────────────────────────────────
  let yPos = height - headerHeight - containerPadY

  const selectedPub = publicacao || (Array.isArray(processo?.Publicacoes) && processo.Publicacoes.length > 0 ? processo.Publicacoes[0] : null)

  const drawField = (label, value, x, y, maxWidth = 180) => {
    const labelText = label + ': '
    const labelW = fontBold.widthOfTextAtSize(labelText, 9)
    page.drawText(labelText, { x, y, size: 9, font: fontBold, color: textColor })
    let val = value ? sanitizeTextForPDF(String(value)) : '-'
    const available = maxWidth - labelW
    if (fontRegular.widthOfTextAtSize(val, 9) > available) {
      while (fontRegular.widthOfTextAtSize(val, 9) > available - fontRegular.widthOfTextAtSize('...', 9) && val.length > 0) {
        val = val.slice(0, -1)
      }
      val += '...'
    }
    page.drawText(val, { x: x + labelW, y, size: 9, font: fontRegular, color: textColor })
  }

  const col1X = margin + containerPadX
  const col2X = margin + containerPadX + 285

  let dataSituacao = '-'
  if (selectedPub?.DataPublicacao) {
    try { dataSituacao = new Date(selectedPub.DataPublicacao).toLocaleDateString('pt-BR') } catch (_) {}
  }

  const valorCausa = processo.Lead?.ValorCausa
    ? `R$ ${parseFloat(processo.Lead.ValorCausa).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`
    : null

  const fields = [
    [['Situação', processo.Lead?.SituacaoLead || 'Suspenso / Sobrestado', col1X, 250], ['Data Situação', dataSituacao, col2X, 280]],
    [['Tipo', processo.Lead?.TipoLead || 'Judicial', col1X, 250], ['Objeto', processo.Lead?.ObjetoSegurado || 'Garantia do Juízo', col2X, 280]],
    [['Esfera', processo.Lead?.Esfera || 'Cível/Fiscal', col1X, 250], ['Valor da Causa', valorCausa, col2X, 280]],
    [['Tribunal', processo.Lead?.Tribunal, col1X, 250], ['Classificação Processo', processo.ClassificacaoProcesso || 'Execução Fiscal', col2X, 280]],
    [['Cliente', processo.Lead?.ReuRazaoSocial, col1X, 250], ['Parte Contrária', processo.Lead?.Reclamante, col2X, 280]],
  ]

  for (const row of fields) {
    for (const [label, value, x, maxW] of row) {
      drawField(label, value, x, yPos, maxW)
    }
    yPos -= 20
  }
  drawField('Processos Relacionados', processo.Lead?.ProcessoRelacionado, col1X, yPos, 530)
  yPos -= 10

  if (!selectedPub) {
    const pdfBytes = await pdfDoc.save()
    return new Blob([pdfBytes], { type: 'application/pdf' })
  }

  // ── Oportunidade ──────────────────────────────────────────────────────────
  yPos -= 10
  const opBoxH = 50
  const opBoxY = yPos - opBoxH

  drawFilledRoundedRect(page, margin, opBoxY, width - margin * 2, opBoxH, 8, secondaryColor)

  if (lawIcon) {
    page.drawImage(lawIcon, { x: margin + 15, y: opBoxY + (opBoxH - iconSize) / 2, width: iconSize, height: iconSize })
  }

  const opTextX = margin + 15 + iconSize + 15
  const opTextY = opBoxY + opBoxH / 2 - 3
  page.drawText('Oportunidade:', { x: opTextX, y: opTextY, size: 12, font: fontBold, color: rgb(0, 0, 0) })

  const temApolice = Array.isArray(processo?.Apolices) && processo.Apolices.length > 0
  const opLabel = fontBold.widthOfTextAtSize('Oportunidade:', 12)
  page.drawText(temApolice ? 'Renovação' : 'Nova Emissão', { x: opTextX + opLabel + 10, y: opTextY, size: 12, font: fontRegular, color: rgb(0, 0, 0) })

  yPos = opBoxY - 10

  // ── Resumo ────────────────────────────────────────────────────────────────
  const resumoFixedH = 300
  const resumoEndY = yPos - resumoFixedH

  drawRoundedRect(page, margin, resumoEndY, width - margin * 2, resumoFixedH, borderRadius, primaryColor, 1)

  const resumoIconY = yPos - iconSize - 10
  if (paperIcon) {
    page.drawImage(paperIcon, { x: margin + containerPadX, y: resumoIconY, width: iconSize, height: iconSize })
  }
  page.drawText('Resumo:', {
    x: margin + containerPadX + iconSize + 15,
    y: resumoIconY + iconSize / 2 - 5,
    size: 12, font: fontBold, color: rgb(0, 0, 0),
  })

  yPos = resumoIconY - 20

  // Resolve resumo text
  const fallbackResumo = 'Trata da suspensão da exigibilidade de débitos não tributários relacionados ao ressarcimento ao SUS, envolvendo a operadora de saúde e o valor total de R$ 24.694.491,09. Argumenta que a garantia será fornecida por meio de seguro garantia, amparada pelo Código Tributário Nacional (art. 151, inc. II) e jurisprudência associada. Ele também aborda a Resolução Normativa ANS nº 502/22, que regulamenta como o cálculo de ressarcimento ao SUS deve ocorrer, aplicando o Índice de Valoração do Ressarcimento (IVR) de 1,5 vezes os valores da tabela SUS. No entanto, o texto critica a falta de auditoria e consistência nos dados administrativos apresentados. A empresa cita que irá realizar apresentação de seguro garantia, posteriormente do despacho de autorização do juiz. Destarte, vale mencionar, que foi dado o despacho inicial apenas para emenda da inicial, sem mencionar a autorização do seguro garantia.'

  let resumoText = ''
  const observacoes = String(processo?.Observacao || '').trim()
  if (observacoes && (observacoes.split(/\s+/).length > 10 || observacoes.length > 10)) {
    resumoText = observacoes
  } else {
    resumoText = selectedPub?.Resumo ? String(selectedPub.Resumo).trim() : ''
    if (selectedPub && !resumoText) {
      const aiResult = await fetchAiResumo(processo, selectedPub)
      if (aiResult?.objetoAcao) resumoText = String(aiResult.objetoAcao).trim()
    }
  }

  resumoText = resumoText || fallbackResumo

  const resumoLines = wrapText(resumoText, width - margin * 2 - containerPadX * 2, fontRegular, 9)
  for (const line of resumoLines) {
    if (yPos < resumoEndY + 5) break
    page.drawText(line, { x: margin + containerPadX, y: yPos, size: 9, font: fontRegular, color: textColor })
    yPos -= 12
  }

  yPos = resumoEndY - 10

  // ── Apólice header ────────────────────────────────────────────────────────
  const apBoxH = 50
  const apBoxY = yPos - apBoxH

  drawFilledRoundedRect(page, margin, apBoxY, width - margin * 2, apBoxH, 8, secondaryColor)

  if (paperAltIcon) {
    page.drawImage(paperAltIcon, { x: margin + 15, y: apBoxY + (apBoxH - iconSize) / 2, width: iconSize, height: iconSize })
  }
  page.drawText('Apólice no Processo:', {
    x: margin + 15 + iconSize + 15,
    y: apBoxY + apBoxH / 2 - 3,
    size: 12, font: fontBold, color: rgb(0, 0, 0),
  })

  yPos = apBoxY - 10

  // ── Apólices table ────────────────────────────────────────────────────────
  const apolices = Array.isArray(processo?.Apolices) ? processo.Apolices : []
  const tableX = margin
  const tableW = width - margin * 2
  const darkGray = rgb(0.13, 0.13, 0.13)
  const borderGray = rgb(0.6, 0.6, 0.6)
  const lightBorder = rgb(0.8, 0.8, 0.8)

  const colPositions = [tableX + 15, tableX + 120, tableX + 225, tableX + 330, tableX + 415, tableX + 460, tableX + 515]
  const colWidths = [
    colPositions[1] - colPositions[0],
    colPositions[2] - colPositions[1],
    colPositions[3] - colPositions[2],
    colPositions[4] - colPositions[3],
    colPositions[5] - colPositions[4],
    colPositions[6] - colPositions[5],
    (tableX + tableW) - colPositions[6],
  ]

  const headers = ['Seguradora', 'Corretora', 'Importância\nSegurada', 'Prêmio', 'Taxa', 'Início da\nVigência', 'Fim da\nVigência']
  const tableStartY = yPos

  // Header top border
  page.drawLine({ start: { x: tableX, y: tableStartY }, end: { x: tableX + tableW, y: tableStartY }, thickness: 1, color: borderGray })

  const headerRowH = 25
  for (let i = 0; i < headers.length; i++) {
    const lines = headers[i].split('\n')
    let yOff = 0
    for (const line of lines) {
      page.drawText(sanitizeTextForPDF(line), {
        x: colPositions[i],
        y: tableStartY - headerRowH + 13 - yOff,
        size: 8, font: fontBold, color: darkGray,
      })
      yOff += 10
    }
  }

  // Header bottom border
  page.drawLine({ start: { x: tableX, y: tableStartY - headerRowH }, end: { x: tableX + tableW, y: tableStartY - headerRowH }, thickness: 1, color: borderGray })

  let dataY = tableStartY - headerRowH
  const dataFontSize = 7.5
  const corretoraFontSize = 6.5
  const cellPadTop = 6
  const cellPadBottom = 4
  const lineSpacingCell = 7

  const numRows = apolices.length > 0 ? Math.min(apolices.length, 5) : 1

  if (apolices.length > 0) {
    for (let rowIdx = 0; rowIdx < numRows; rowIdx++) {
      const a = apolices[rowIdx]
      const dataRow = [
        sanitizeTextForPDF(String(a.Seguradora || '')),
        sanitizeTextForPDF(String(a.Corretora || '')),
        a.ImportanciaSegurada ? sanitizeTextForPDF(parseFloat(a.ImportanciaSegurada).toLocaleString('pt-BR', { minimumFractionDigits: 2 })) : '-',
        a.Premio ? sanitizeTextForPDF(`R$ ${parseFloat(a.Premio).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`) : '-',
        a.Taxa ? sanitizeTextForPDF(`${parseFloat(a.Taxa).toFixed(2).replace('.', ',')}%`) : '-',
        sanitizeTextForPDF(a.InicioVigencia || '-'),
        sanitizeTextForPDF(a.FimVigencia || '-'),
      ]

      const cellLines = []
      const cellFontSizes = []
      let maxLines = 1

      for (let i = 0; i < dataRow.length; i++) {
        const fs = i === 1 ? corretoraFontSize : dataFontSize
        const lines = wrapTextInCell(dataRow[i], colWidths[i], fontRegular, fs)
        cellLines.push(lines)
        cellFontSizes.push(fs)
        maxLines = Math.max(maxLines, lines.length)
      }

      const maxFs = Math.max(...cellFontSizes)
      const textH = maxFs + (maxLines > 1 ? (maxLines - 1) * (maxFs + lineSpacingCell) : 0)
      const rowH = cellPadTop + 5 + textH + cellPadBottom
      const rowTopY = dataY
      dataY -= rowH

      for (let i = 0; i < cellLines.length; i++) {
        const fs = cellFontSizes[i]
        const vOffset = (maxFs - fs) / 2
        let lineY = rowTopY - cellPadTop - 5 + vOffset
        for (const line of cellLines[i]) {
          page.drawText(line, { x: colPositions[i], y: lineY, size: fs, font: fontRegular, color: textColor })
          lineY -= (fs + lineSpacingCell)
        }
      }

      page.drawLine({ start: { x: tableX, y: dataY }, end: { x: tableX + tableW, y: dataY }, thickness: 1, color: lightBorder })
    }
  } else {
    dataY -= 22
    page.drawLine({ start: { x: tableX, y: dataY }, end: { x: tableX + tableW, y: dataY }, thickness: 1, color: lightBorder })
  }

  const pdfBytes = await pdfDoc.save()
  return new Blob([pdfBytes], { type: 'application/pdf' })
}

export function downloadPDF(blob, filename = 'processo.pdf') {
  const url = window.URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  link.click()
  window.URL.revokeObjectURL(url)
}
